import { type ApiSchemas, call, type Result } from '@rental/contracts/client'
import type { PaymentMethod, RefundMethod } from '@rental/domain'
import { format, formatDateTime, formatEuro } from '@rental/i18n'
import { Button, Checkbox, Field, Notice } from '@rental/ui'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useId, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { rememberedPoint } from '../cash/drawer'
import { StatusChip } from '../components/StatusChip'
import { ReasonList } from '../components/SubPanel'
import { ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import { romeNow } from '../time'
import {
  type DocumentDraft,
  documentBody,
  documentsOf,
  type FiscalMoney,
  newDraft,
} from './document'
import { FiscalFields } from './FiscalFields'

type StaffFiscalDocument = ApiSchemas['StaffFiscalDocument']

/** The screens that show fiscal documents, or money without one. */
export const FISCAL_QUERY_KEY = ['fiscal'] as const

export async function refreshFiscal(queryClient: QueryClient, code?: string): Promise<void> {
  await Promise.all(
    [FISCAL_QUERY_KEY, ['reports', 'open'], ...(code ? [['booking', code]] : [])].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )
}

/** The money a document is for, as the screens know it. */
export interface DocumentMoney {
  money: FiscalMoney
  id: string
  method: PaymentMethod | RefundMethod
  amountCents: number
  /** When it was taken, or given back: the printed time a document typed later starts from. */
  at: string
  /** The booking, to refresh it. */
  code: string
}

/** One document in words: kind, number, when printed, and the till's point. */
export function documentText(
  document: StaffFiscalDocument,
  locale: Parameters<typeof formatDateTime>[0],
  m: ReturnType<typeof useLocale>['messages'],
): string {
  const values = {
    kind: m.fiscal.kinds[document.kind],
    number: document.number,
    date: formatDateTime(locale, document.issuedAt),
  }
  return document.location
    ? format(m.fiscal.linePoint, { ...values, point: document.location.name })
    : format(m.fiscal.line, values)
}

/** A form sent once: busy while it goes, the API's problem shown, takeovers handled. */
function useSend() {
  const { takeOver } = useAccess()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  async function send(request: () => Promise<Result<unknown>>): Promise<boolean> {
    setProblem(null)
    setBusy(true)
    const result = await request()
    setBusy(false)
    if (result.ok) return true
    if (!takeOver(result.problem)) setProblem(result.problem)
    return false
  }
  return { send, busy, problem }
}

/**
 * The document of money that has none yet, typed from what was printed:
 * the printed time starts from when the money moved, which decides the Z it
 * belongs to.
 */
export function RecordDocument({
  target,
  onDone,
  onCancel,
}: {
  target: DocumentMoney
  onDone: () => void
  onCancel: () => void
}) {
  const { locale, messages: m } = useLocale()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<DocumentDraft>(() =>
    newDraft(target.money, target.method, {
      point: rememberedPoint(),
      printedAt: new Date(target.at),
    }),
  )
  const [missing, setMissing] = useState<'point' | 'printed' | null>(null)
  const { send, busy, problem } = useSend()
  const pointFromDrawer = target.method === 'cash'
  const reasonsId = useId()
  const reasons = draft.number.trim() || busy ? [] : [m.fiscal.why.number]

  async function submit(event: FormEvent) {
    event.preventDefault()
    const outcome = documentBody(draft, { pointFromDrawer })
    if (!outcome.ok) {
      setMissing(outcome.missing)
      return
    }
    if (!outcome.document) return
    setMissing(null)
    const body =
      target.money === 'payment'
        ? { paymentId: target.id, document: outcome.document }
        : { refundId: target.id, document: outcome.document }
    if (await send(() => call(api.POST('/v1/staff/fiscal/documents', { body })))) {
      await refreshFiscal(queryClient, target.code)
      onDone()
    }
  }

  // Named, not a heading: it opens under an h2 on Fiscale and an h3 on the booking.
  const title = format(m.fiscal.recordTitle, { amount: formatEuro(target.amountCents, locale) })
  return (
    <form
      onSubmit={submit}
      aria-label={title}
      className="flex flex-col gap-3 rounded-sm bg-surface-well p-4"
      noValidate
    >
      <p className="text-lg font-bold text-brand-strong">{title}</p>
      <FiscalFields
        name={`record-${target.id}`}
        money={target.money}
        draft={draft}
        onChange={setDraft}
        pointFromDrawer={pointFromDrawer}
        printed="shown"
        required
        missing={missing}
      />
      <ProblemNotice problem={problem} />
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="staff"
          busy={busy}
          disabled={reasons.length > 0}
          aria-describedby={reasons.length > 0 ? reasonsId : undefined}
        >
          {m.fiscal.recordSubmit}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-sm"
          onClick={onCancel}
          disabled={busy}
        >
          {m.fiscal.cancel}
        </Button>
      </div>
      <ReasonList id={reasonsId} reasons={reasons} />
    </form>
  )
}

/**
 * A document typed wrong: superseded with a reason and kept as it was; the
 * right one may follow, starting from the wrong one's fields.
 */
export function CorrectDocument({
  wrong,
  target,
  onDone,
  onCancel,
}: {
  wrong: StaffFiscalDocument
  target: DocumentMoney
  onDone: () => void
  onCancel: () => void
}) {
  const { messages: m } = useLocale()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [withRight, setWithRight] = useState(true)
  const [draft, setDraft] = useState<DocumentDraft>(() => ({
    kind: wrong.kind,
    source: wrong.source === 'till' ? 'till' : 'other',
    number: wrong.number,
    locationId: wrong.location?.id ?? rememberedPoint(),
    printed: romeNow(new Date(wrong.issuedAt)),
  }))
  const [missing, setMissing] = useState<'point' | 'printed' | null>(null)
  const { send, busy, problem } = useSend()
  const pointFromDrawer = target.method === 'cash'
  const reasonOk = reason.trim().length >= 3
  const ready = reasonOk && (!withRight || draft.number.trim() !== '')
  const reasonsId = useId()
  const reasons = busy
    ? []
    : [
        ...(reasonOk ? [] : [m.fiscal.why.reason]),
        ...(withRight && draft.number.trim() === '' ? [m.fiscal.why.number] : []),
      ]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!ready) return
    let right: ReturnType<typeof documentBody> | null = null
    if (withRight) {
      right = documentBody(draft, { pointFromDrawer })
      if (!right.ok) {
        setMissing(right.missing)
        return
      }
    }
    setMissing(null)
    const body = {
      reason: reason.trim(),
      ...(right?.ok && right.document ? { document: right.document } : {}),
    }
    const sent = await send(() =>
      call(
        api.POST('/v1/staff/fiscal/documents/{id}/correction', {
          params: { path: { id: wrong.id } },
          body,
        }),
      ),
    )
    if (sent) {
      await refreshFiscal(queryClient, target.code)
      onDone()
    }
  }

  const title = format(m.fiscal.correctTitle, { number: wrong.number })
  return (
    <form
      onSubmit={submit}
      aria-label={title}
      className="flex flex-col gap-3 rounded-sm bg-surface-well p-4"
      noValidate
    >
      <p className="text-lg font-bold text-brand-strong">{title}</p>
      <Field
        label={m.fiscal.correctReason}
        hint={m.fiscal.correctReasonHint}
        required
        error={reason.trim().length > 0 && !reasonOk ? m.refund.reasonShort : null}
      >
        {(control) => (
          <StaffInput
            {...control}
            maxLength={500}
            autoComplete="off"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
      <Checkbox
        label={m.fiscal.correctWithRight}
        checked={withRight}
        onChange={(event) => setWithRight(event.target.checked)}
      />
      {withRight ? (
        <FiscalFields
          name={`correct-${wrong.id}`}
          money={target.money}
          draft={draft}
          onChange={setDraft}
          pointFromDrawer={pointFromDrawer}
          printed="shown"
          required
          missing={missing}
        />
      ) : null}
      <ProblemNotice problem={problem} />
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="staff"
          busy={busy}
          disabled={!ready && !busy}
          aria-describedby={reasons.length > 0 ? reasonsId : undefined}
        >
          {m.fiscal.correctSubmit}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-sm"
          onClick={onCancel}
          disabled={busy}
        >
          {m.fiscal.cancel}
        </Button>
      </div>
      <ReasonList id={reasonsId} reasons={reasons} />
    </form>
  )
}

/**
 * Under a payment or a refund on the booking: its document, or that it has
 * none, the corrected ones with why, and for those who take money the way
 * to type or correct it. S9 (Q-120): money of the old site gets none here,
 * so it is said plainly, with nothing to type and nothing missing.
 */
export function MoneyDocuments({
  documents,
  target,
  here,
  mayType,
}: {
  documents: readonly StaffFiscalDocument[]
  target: DocumentMoney
  /** The server's word (fiscalDocumentHere): false for money of the old site. */
  here: boolean
  /** S9: the booking's answer (fiscalDocument): typed at the office, never on a trip. */
  mayType: boolean
}) {
  const { messages: m } = useLocale()
  if (!here) {
    return (
      <p className="text-sm text-text-muted" data-testid={`fiscal-${target.id}`}>
        {m.fiscal.notHere}
      </p>
    )
  }
  return <MoneyDocumentsHere documents={documents} target={target} mayType={mayType} />
}

function MoneyDocumentsHere({
  documents,
  target,
  mayType,
}: {
  documents: readonly StaffFiscalDocument[]
  target: DocumentMoney
  mayType: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { live, corrected } = documentsOf(
    documents,
    target.money === 'payment' ? { paymentId: target.id } : { refundId: target.id },
  )
  const [open, setOpen] = useState<'record' | 'correct' | null>(null)
  const [done, setDone] = useState<'recorded' | 'corrected' | null>(null)

  return (
    <div className="flex flex-col gap-1 text-sm" data-testid={`fiscal-${target.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {live ? (
          <span>{documentText(live, locale, m)}</span>
        ) : (
          <StatusChip tone="warning">{m.fiscal.none}</StatusChip>
        )}
        {mayType && open === null ? (
          <Button
            type="button"
            variant="link"
            onClick={() => {
              setDone(null)
              setOpen(live ? 'correct' : 'record')
            }}
          >
            {live ? m.fiscal.correct : m.fiscal.record}
          </Button>
        ) : null}
      </div>
      {corrected.map((document) => (
        <p key={document.id} className="text-text-muted">
          {format(m.fiscal.superseded, {
            kind: m.fiscal.kinds[document.kind],
            number: document.number,
            date: document.supersededAt ? formatDateTime(locale, document.supersededAt) : '—',
            reason: document.supersededReason ?? '',
          })}
        </p>
      ))}
      {done ? (
        <Notice tone="success" live="polite">
          <p>{m.fiscal[done]}</p>
        </Notice>
      ) : null}
      {open === 'record' ? (
        <RecordDocument
          target={target}
          onDone={() => {
            setOpen(null)
            setDone('recorded')
          }}
          onCancel={() => setOpen(null)}
        />
      ) : null}
      {open === 'correct' && live ? (
        <CorrectDocument
          wrong={live}
          target={target}
          onDone={() => {
            setOpen(null)
            setDone('corrected')
          }}
          onCancel={() => setOpen(null)}
        />
      ) : null}
    </div>
  )
}
